import { useState, createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';

export const SipWebsocketContext = createContext(null);

export const useSipWebsocket = () => {
	return useContext(SipWebsocketContext);
};

const RECONNECT_BASE_DELAY = 3000;
const RECONNECT_MAX_DELAY = 30000;
const PING_INTERVAL = 28000;

const buildWsUrl = () => {
	const secure = process.env.REACT_APP_SIP_WS_SECURE === 'true';
	const host = process.env.REACT_APP_SIP_WEBSOCKET || '';
	const port = process.env.REACT_APP_SIP_WS_PORT || '';
	const rawPath = process.env.REACT_APP_SIP_WS_PATH || '';
	const path = rawPath.startsWith('/') ? rawPath : '/' + rawPath;

	return 'ws' + (secure ? 's' : '') + '://' + host + (port ? ':' + port : '') + path;
};

const parseIncomingJson = async data => {
	if (typeof data === 'string') {
		return JSON.parse(data);
	}

	if (data instanceof Blob) {
		const text = await data.text();
		return JSON.parse(text);
	}

	return null;
};

export default function SipWebsocketProvider(props) {
	const [sipId, setSipId] = useState(null);
	const [initialized, setInitialized] = useState(false);
	const [error, setError] = useState(null);
	const [message, setMessage] = useState(null);

	const websocketRef = useRef(null);
	const pingTimerRef = useRef(null);
	const reconnectTimerRef = useRef(null);
	const reconnectAttemptRef = useRef(0);
	const shouldReconnectRef = useRef(false);
	const manualCloseRef = useRef(false);
	const openConnectionRef = useRef(null);
	const messageCountRef = useRef(0);

	const user = useSelector(({ auth }) => auth.user);

	const clearPingTimer = useCallback(() => {
		if (pingTimerRef.current) {
			clearInterval(pingTimerRef.current);
			pingTimerRef.current = null;
		}
	}, []);

	const clearReconnectTimer = useCallback(() => {
		if (reconnectTimerRef.current) {
			clearTimeout(reconnectTimerRef.current);
			reconnectTimerRef.current = null;
		}
	}, []);

	const sendMessage = useCallback(cmd => {
		if (!cmd || !websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
			return;
		}

		console.log('Submit command:');
		console.log(cmd);

		if (typeof cmd === 'string') {
			websocketRef.current.send(cmd);
			return;
		}

		websocketRef.current.send(JSON.stringify(cmd));
	}, []);

	const handleMessage = useCallback(
		async event => {
			try {
				const parsedData = await parseIncomingJson(event.data);

				if (parsedData == null) {
					console.warn('Unsupported websocket message format:', event.data);
					return;
				}

				messageCountRef.current += 1;
				setMessage(parsedData);
			} catch (err) {
				console.error('Error parsing websocket message:', err);
				setError(err);
			}
		},
		[]
	);

	const scheduleReconnect = useCallback(() => {
		if (!shouldReconnectRef.current) {
			return;
		}

		if (reconnectTimerRef.current) {
			return;
		}

		const delay = Math.min(RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptRef.current), RECONNECT_MAX_DELAY);
		reconnectAttemptRef.current += 1;

		console.log('WebSocket reconnect scheduled in ' + delay + ' ms');

		reconnectTimerRef.current = setTimeout(() => {
			reconnectTimerRef.current = null;

			if (shouldReconnectRef.current && openConnectionRef.current) {
				openConnectionRef.current();
			}
		}, delay);
	}, []);

	const openConnection = useCallback(() => {
		if (!shouldReconnectRef.current) {
			return;
		}

		if (websocketRef.current && (websocketRef.current.readyState === WebSocket.OPEN || websocketRef.current.readyState === WebSocket.CONNECTING)) {
			return;
		}

		clearReconnectTimer();
		manualCloseRef.current = false;

		console.log('open connection');

		const ws = new WebSocket(buildWsUrl());
		websocketRef.current = ws;

		ws.onopen = () => {
			reconnectAttemptRef.current = 0;
			setInitialized(true);
			setError(null);

			clearPingTimer();
			pingTimerRef.current = setInterval(() => {
				sendMessage({ type: 'PING', payload: { ts: Date.now() } });
			}, PING_INTERVAL);
		};

		ws.onmessage = handleMessage;

		ws.onerror = event => {
			console.error('WebSocket error:', event);

			if (ws.readyState !== WebSocket.OPEN) {
				setError(event);
			}
		};

		ws.onclose = event => {
			console.log('close connection', event.code, event.reason);

			websocketRef.current = null;
			clearPingTimer();
			setInitialized(false);
			setSipId(null);

			if (!manualCloseRef.current && shouldReconnectRef.current) {
				scheduleReconnect();
			}
		};
	}, [clearPingTimer, clearReconnectTimer, handleMessage, scheduleReconnect, sendMessage]);

	openConnectionRef.current = openConnection;

	const closeConnection = useCallback(
		manual => {
			console.log('close connection');

			manualCloseRef.current = manual !== false;

			if (manual !== false) {
				shouldReconnectRef.current = false;
				clearReconnectTimer();
			}

			clearPingTimer();
			setInitialized(false);
			setSipId(null);

			if (websocketRef.current) {
				const ws = websocketRef.current;
				websocketRef.current = null;

				if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
					ws.close();
				}
			}
		},
		[clearPingTimer, clearReconnectTimer]
	);

	useEffect(() => {
		const shouldOpen = !!(user && user.data && user.data.name !== 'Guest');

		shouldReconnectRef.current = shouldOpen;

		if (shouldOpen) {
			openConnection();
		} else {
			closeConnection(true);
		}

		return () => {
			closeConnection(true);
		};
	}, [user, openConnection, closeConnection]);

	const value = {
		sipId,
		initialized,
		error,
		sendMessage,
		message,
		messageCountRef
	};

	return <SipWebsocketContext.Provider value={value}>{props.children}</SipWebsocketContext.Provider>;
}